import React, { useEffect, useState } from "react";
import { getTeam } from "../api";
import { useTranslation } from "../LanguageContext";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

export default function TeamPage({ slug }) {
  const { t } = useTranslation();
  const [team, setTeam] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    getTeam(slug).then((data) => {
      if (!data) setNotFound(true);
      else setTeam(data);
    });
  }, [slug]);

  if (notFound) {
    return (
      <div style={styles.shell}>
        <header style={isMobile ? styles.headerMobile : styles.header}>
          <a href="https://results.nyrr.run" style={styles.logo}>NYRR Head to Head</a>
        </header>
        <div style={styles.empty}>{t.teamNotFound}</div>
      </div>
    );
  }

  if (!team) {
    return (
      <div style={styles.shell}>
        <header style={isMobile ? styles.headerMobile : styles.header}>
          <a href="https://results.nyrr.run" style={styles.logo}>NYRR Head to Head</a>
        </header>
        <div style={styles.empty}>{t.loading}</div>
      </div>
    );
  }

  return (
    <div style={styles.shell}>
      <header style={isMobile ? styles.headerMobile : styles.header}>
        <div>
          <div style={isMobile ? styles.teamNameMobile : styles.teamName}>{team.team_name}</div>
          <div style={styles.meta}>{team.runner_count} {t.runners}</div>
        </div>
        <a href="https://results.nyrr.run" style={styles.backLink}>{t.allRunners}</a>
      </header>

      <div style={isMobile ? styles.tableWrapperMobile : styles.tableWrapper}>
        <table style={isMobile ? styles.tableMobile : styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{t.colHash}</th>
              <th style={styles.th}>{t.colName}</th>
              {!isMobile && <th style={styles.th}>{t.colLocation}</th>}
              {!isMobile && <th style={styles.th}>{t.colAge}</th>}
              {!isMobile && <th style={styles.th}>{t.colGender}</th>}
              <th style={{ ...styles.th, textAlign: "right" }}>{t.colRaces}</th>
            </tr>
          </thead>
          <tbody>
            {team.runners.map((r, i) => (
              <tr key={r.runner_id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                <td style={{ ...styles.td, color: "#aaa", width: isMobile ? "32px" : "48px", fontSize: isMobile ? "0.78rem" : undefined }}>{i + 1}</td>
                <td style={isMobile ? styles.tdMobile : styles.td}>
                  {r.first_name} {r.last_name}
                  {isMobile && (
                    <div style={styles.tdSub}>
                      {[r.gender, r.age ? `${t.ageMobile} ${r.age}` : null, r.city].filter(Boolean).join(" · ") || "—"}
                    </div>
                  )}
                </td>
                {!isMobile && (
                  <td style={{ ...styles.td, color: "#666" }}>
                    {[r.city, r.state_province].filter(Boolean).join(", ") || "—"}
                  </td>
                )}
                {!isMobile && <td style={{ ...styles.td, color: "#666" }}>{r.age ?? "—"}</td>}
                {!isMobile && <td style={{ ...styles.td, color: "#666" }}>{r.gender ?? "—"}</td>}
                <td style={{ ...styles.td, textAlign: "right", color: "#888", fontSize: isMobile ? "0.82rem" : undefined }}>
                  {r.races_count ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  shell: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    background: "#f7f7f8",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    background: "#fff",
    borderBottom: "1px solid #e1e1e1",
    flexShrink: 0,
  },
  headerMobile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    background: "#fff",
    borderBottom: "1px solid #e1e1e1",
    flexShrink: 0,
  },
  logo: {
    fontWeight: 700,
    fontSize: "1rem",
    color: "#c4622d",
    textDecoration: "none",
  },
  teamName: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: "#c4622d",
  },
  teamNameMobile: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: "#c4622d",
    maxWidth: "180px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  meta: {
    fontSize: "0.8rem",
    color: "#aaa",
    marginTop: "2px",
  },
  backLink: {
    fontSize: "0.85rem",
    color: "#c4622d",
    textDecoration: "none",
    flexShrink: 0,
  },
  empty: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#aaa",
    fontSize: "0.9rem",
  },
  tableWrapper: {
    flex: 1,
    overflowY: "auto",
    padding: "24px",
  },
  tableWrapperMobile: {
    flex: 1,
    overflowY: "auto",
    padding: "12px",
  },
  table: {
    width: "100%",
    maxWidth: "640px",
    margin: "0 auto",
    borderCollapse: "collapse",
    fontSize: "0.9rem",
    background: "#fff",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  tableMobile: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.85rem",
    background: "#fff",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  th: {
    textAlign: "left",
    padding: "10px 16px",
    background: "#f8f8f8",
    borderBottom: "2px solid #e1e1e1",
    fontWeight: 600,
    color: "#444",
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 16px",
    borderBottom: "1px solid #f0f0f0",
    color: "#333",
  },
  tdMobile: {
    padding: "10px 12px",
    borderBottom: "1px solid #f0f0f0",
    color: "#333",
    fontSize: "0.88rem",
  },
  tdSub: {
    fontSize: "0.75rem",
    color: "#999",
    marginTop: "2px",
  },
  trEven: { background: "#fff" },
  trOdd: { background: "#fafafa" },
};
